import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import Box, { BoxProps } from 'ui-box';
import {
  Button,
  Card,
  Container,
  defaultFills,
  Grid,
  Icon,
  majorScale,
  minorScale,
  PageContainer,
  Section,
  PageHeader,
  Spinner,
  Tag,
} from '../../../npaper';
import { useStores } from '../../../stores';
import {
  IIntegration,
  IIntegrationType,
} from '../../../stores/IntegrationStore';
import { IQueryCache } from '../../../stores/utils/modelWithCRUD';
import { IntegrationDetailsDialog } from './DetailsDialog';

const IntegrationCard: React.FC<
  BoxProps<'div'> & {
    integrationType: IIntegrationType;
    integration?: IIntegration;
  }
> = observer(({ integrationType, integration, ...props }) => {
  const [edit, setEdit] = useState(false);

  return (
    <Box marginBottom={majorScale(4)} {...props}>
      <IntegrationDetailsDialog
        integrationType={integrationType}
        integration={integration}
        isShown={edit}
        onClose={() => setEdit(false)}
      />
      <Card width="100%" paddingTop="50%" position="relative">
        {integration && (
          <Tag
            position="absolute"
            top={majorScale(1)}
            left={majorScale(1)}
            intent={integration.activated_at ? 'success' : 'warning'}
          >
            {integration.activated_at ? (
              <FormattedMessage defaultMessage="Active" />
            ) : (
              <FormattedMessage defaultMessage="Inactive" />
            )}
          </Tag>
        )}
        <div className="w-full h-full p-6 top-0 left-0 absolute flex items-center justify-center">
          {integrationType.info.icon ? (
            <img
              src={integrationType.info.icon}
              alt={integrationType.info.key}
              style={{ objectFit: 'contain' }}
              width="100%"
              height="100%"
            />
          ) : (
            <Icon icon="media" color={defaultFills.muted} size={32} />
          )}
        </div>
      </Card>
      <Box
        className="p medium"
        marginTop={majorScale(1)}
        marginBottom={minorScale(1)}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <span>
          {integrationType.info.name}{' '}
          <span className="p small muted">{integrationType.info.version}</span>
        </span>

        <Button
          small
          intent="info"
          minimal
          flexShrink={0}
          onClick={() => setEdit(true)}
        >
          {integration ? 'Edit integration' : 'Setup integration'}
        </Button>
      </Box>
      <Box className="p small muted">{integrationType.info.description}</Box>
    </Box>
  );
});

export const IntegrationsPage = observer(() => {
  const history = useHistory();
  const { integrationStore } = useStores();

  const [integrationTypes, setIntegrationTypes] =
    useState<IIntegrationType[]>();

  const [integrationCache, setIntegrationCache] =
    useState<IQueryCache<IIntegration>>();
  const [editIntegration, setEditIntegration] = useState<IIntegration>();

  useEffect(() => {
    let isSub = true;
    integrationStore
      .getAllTypes()
      .then((types: IIntegrationType[]) => isSub && setIntegrationTypes(types));
    return () => {
      isSub = false;
    };
  }, [integrationStore]);

  useEffect(() => {
    let isSub = true;
    integrationStore
      .getAll()
      .then(
        (cache: IQueryCache<IIntegration>) =>
          isSub && setIntegrationCache(cache)
      );
    return () => {
      isSub = false;
    };
  }, [integrationStore]);

  const location = useLocation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const clientCallBackKey = searchParams.get('client_callback_key');
    if (clientCallBackKey) {
      const configuration: any = {};
      Array.from(searchParams.keys()).forEach((key) => {
        configuration[key] = searchParams.get(key);
      });
      delete configuration['client_callback_key'];
      let isSub = true;
      integrationStore
        .createItem({
          key: clientCallBackKey,
          configuration,
        })
        .then(
          (integration: IIntegration) =>
            isSub && setEditIntegration(integration)
        )
        .catch(() => {
          integrationStore
            .getAll({ filter: { key: clientCallBackKey } })
            .then(
              (res: IQueryCache<IIntegration>) =>
                res.items.length > 0 &&
                isSub &&
                setEditIntegration(res.items[0])
            );
          // Do nothing?
        });
      return () => {
        isSub = false;
      };
    }
  }, [integrationStore, location.search]);

  return (
    <PageContainer>
      {editIntegration && (
        <IntegrationDetailsDialog
          integration={editIntegration}
          isShown={!!editIntegration}
          onClose={() => setEditIntegration(undefined)}
          integrationType={editIntegration.integration_type}
        />
      )}
      <Container>
        <Button
          iconLeft="arrow-left"
          marginBottom={majorScale(2)}
          onClick={() =>
            history.length > 0 ? history.goBack() : history.push('/settings')
          }
        >
          Indstillinger
        </Button>

        <PageHeader title="Integrationer" />

        <Section title="Aktive integrationer">
          {!integrationCache ? (
            <Spinner size={16} />
          ) : integrationCache.items.length === 0 ? (
            <p className="muted">Du har ingen aktive integrationer</p>
          ) : (
            <Grid>
              {integrationCache.items.map((integration) => (
                <IntegrationCard
                  key={integration.id}
                  className="col-span-3"
                  integrationType={integration.integration_type}
                  integration={integration}
                />
              ))}
            </Grid>
          )}
        </Section>

        <Section title="Integrationsmuligheder">
          <Grid>
            {integrationTypes &&
              integrationTypes.map((type) => (
                <IntegrationCard
                  key={type.info.key}
                  className="col-span-3"
                  integrationType={type}
                  integration={integrationCache?.items.find(
                    (i) => i.key === type.info.key
                  )}
                />
              ))}
          </Grid>
        </Section>
      </Container>
    </PageContainer>
  );
});
